/**
 * /onboarding — post-sign-up bridge.
 *
 * Creates the first organisation for a signed-in user who genuinely
 * has none, then hands off to `/`.
 *
 * Flow:
 *   useUser() → signed in, no active org
 *   already a member of something? → setActive(that org) → '/'
 *   otherwise → createOrganization({ name }) → setActive → '/'
 *
 * **Membership is checked before the form renders**, and that check is
 * the point of this screen's guard. `AuthGate` routes here on "no
 * ACTIVE org", which is not the same as "no org" — an invited partner
 * whose org didn't activate (ticket redeemed in a tab that lost the
 * `setActive`, a session restored without an org claim) arrives here
 * already belonging to the org they were invited to. Offering them the
 * create form at that point hands them a second, personal workspace
 * and silently strands them outside their team's data. So: if they
 * belong to anything, we activate it and leave.
 *
 * For the same reason the name field is no longer pre-filled with
 * "<FirstName>'s workspace". Clickweave workspaces are companies, and
 * a personal-sounding default invited people to make exactly the org
 * this screen is trying to prevent.
 *
 * This is the minimum viable onboarding surface. Stage 5 replaces
 * with a richer stepper (team invite, first key, first origin).
 */

import { Logo } from '@/components/shell/Logo';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { mapClerkError } from '@/lib/mapClerkError';
import { useAuth, useOrganizationList, useUser } from '@clerk/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage(): ReactElement {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth();
  const { user } = useUser();
  const orgList = useOrganizationList({ userMemberships: { infinite: false } });
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const firstName = user?.firstName ?? undefined;

  // Memberships are the difference between "has no org" and "has no
  // ACTIVE org". Only the former should ever see the create form.
  const memberships = orgList.isLoaded ? (orgList.userMemberships.data ?? []) : [];
  const existingOrgId = memberships[0]?.organization.id;
  const membershipsResolved = orgList.isLoaded;

  // Route away if we shouldn't be here.
  useEffect(() => {
    if (!authLoaded) return;
    if (!isSignedIn) {
      navigate({ to: '/sign-in', replace: true });
      return;
    }
    if (orgId) {
      navigate({ to: '/', replace: true });
    }
  }, [authLoaded, isSignedIn, orgId, navigate]);

  // Already a member of something — activate it instead of offering a
  // second, personal workspace. Fires once; `orgId` flipping non-null
  // sends the effect above to `/`.
  const adoptingRef = useRef(false);
  useEffect(() => {
    if (!authLoaded || !isSignedIn || orgId) return;
    if (!orgList.isLoaded || !orgList.setActive || !existingOrgId) return;
    if (adoptingRef.current) return;
    adoptingRef.current = true;
    void orgList
      .setActive({ organization: existingOrgId })
      .then(() => navigate({ to: '/', replace: true }))
      // If activation fails the user is stuck on a spinner otherwise;
      // release the latch so a reload can retry.
      .catch(() => {
        adoptingRef.current = false;
      });
  }, [authLoaded, isSignedIn, orgId, orgList, existingOrgId, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!orgList.isLoaded || !orgList.createOrganization || !orgList.setActive) return;
    if (!name.trim()) {
      setFieldErrors({ name: 'Give your workspace a name.' });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    try {
      const org = await orgList.createOrganization({ name: name.trim() });
      await orgList.setActive({ organization: org.id });
      navigate({ to: '/', replace: true });
    } catch (err) {
      const mapped = mapClerkError(err);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
      setSubmitting(false);
    }
  }

  // Hold the spinner until memberships have resolved (and through the
  // adoption hop). Rendering the create form during that window is the
  // race that produced personal workspaces for invited users.
  if (!authLoaded || !isSignedIn || orgId || !membershipsResolved || existingOrgId) {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center">
        <Spinner className="size-5 text-[var(--color-mute)]" />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center justify-center gap-2 text-[var(--color-accent-brand)]">
          <Logo size={28} />
          <span className="text-base font-semibold text-[var(--color-ink)]">Clickweave</span>
        </div>
        <div
          className={
            'rounded-[var(--radius-panel)] border border-[var(--color-line)] ' +
            'bg-[var(--color-paper)] p-8 shadow-[var(--shadow-panel)]'
          }
        >
          <header className="space-y-1 text-center">
            <h1 className="text-xl font-semibold text-[var(--color-ink)]">
              {firstName ? `Welcome, ${firstName}.` : 'Welcome to Clickweave.'}
            </h1>
            <p className="text-sm text-[var(--color-mute)]">
              Name your workspace after your company — teammates you invite will join it.
            </p>
          </header>
          <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
            <Field>
              <FieldLabel htmlFor="org-name">Workspace name</FieldLabel>
              <Input
                id="org-name"
                autoFocus
                placeholder="Acme Inc"
                value={name}
                onChange={(event) => setName((event.target as HTMLInputElement).value)}
                aria-invalid={fieldErrors.name ? true : undefined}
                required
              />
              {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
            </Field>

            {formError && (
              <div
                className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
                role="alert"
              >
                {formError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Creating workspace…' : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
